import { Reply as ReplyIcon, Send as SendIcon } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardContent, Divider, Stack, TextField } from '@mui/material';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { ReviewComment } from '../../models/ReviewRequest';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import { StyledTypography } from '../styles/styles';

interface ReviewCommentsProps {
  comments: ReviewComment[];
  onCommentSubmit: (
    comment: string,
    referencedItem?: string,
    contentText?: string,
    parentCommentId?: string,
  ) => void;
  tone?: keyof typeof Palette;
}

interface CommentItemProps {
  comment: ReviewComment;
  onReply: (
    parentCommentId: string,
    comment: string,
    referencedItem?: string,
    contentText?: string,
  ) => void;
  tone: keyof typeof Palette;
  level?: number;
}

function CommentItem({ comment, onReply, tone, level = 0 }: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;

    setSubmitting(true);
    try {
      await onReply(comment.externalId, replyText);
      setReplyText('');
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{ ml: level * 3 }}>
      <Card variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              {getInitials(comment.submittedBy.name)}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <StyledTypography tone={tone} variant="subtitle2">
                  {comment.submittedBy.name}
                </StyledTypography>
                <StyledTypography tone={tone} variant="caption" color="text.secondary">
                  {format(new Date(comment.createdAt), "MMM d, yyyy 'at' h:mm a")}
                </StyledTypography>
              </Stack>

              {comment.referencedItem && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <StyledTypography tone={tone} variant="caption" color="text.secondary">
                    Referenced: {comment.referencedItem}
                  </StyledTypography>
                </Box>
              )}

              {comment.contentText && (
                <Box
                  sx={{
                    mb: 1,
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <StyledTypography
                    tone={tone}
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: 'block' }}
                  >
                    Content being referenced:
                  </StyledTypography>
                  <StyledTypography tone={tone} variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{comment.contentText}"
                  </StyledTypography>
                </Box>
              )}

              <StyledTypography tone={tone} variant="body2" sx={{ mb: 1 }}>
                {comment.comments}
              </StyledTypography>

              <Button
                size="small"
                startIcon={<ReplyIcon />}
                onClick={() => setShowReplyForm(!showReplyForm)}
                sx={{ mb: 1 }}
              >
                Reply
              </Button>

              {showReplyForm && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Write a reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleReplySubmit}
                      disabled={!replyText.trim() || submitting}
                    >
                      {submitting ? 'Sending...' : 'Send Reply'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowReplyForm(false);
                        setReplyText('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Render replies */}
      {comment.replies.map((reply) => (
        <CommentItem
          key={reply.externalId}
          comment={reply}
          onReply={onReply}
          tone={tone}
          level={level + 1}
        />
      ))}
    </Box>
  );
}

export default function ReviewComments({
  comments,
  onCommentSubmit,
  tone = 'tone1',
}: ReviewCommentsProps) {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await onCommentSubmit(newComment);
      setNewComment('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {/* New Comment Form */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Stack direction="row" justifyContent="flex-end">
          <StyledButton
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Comment'}
          </StyledButton>
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Comments List */}
      {comments.length === 0 ? (
        <StyledTypography tone={tone} color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No comments yet. Be the first to add a comment!
        </StyledTypography>
      ) : (
        <Stack spacing={2}>
          {comments.map((comment) => (
            <CommentItem
              key={comment.externalId}
              comment={comment}
              onReply={onCommentSubmit}
              tone={tone}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
